/**
 * Local structural footer config (Phase 4, R-401 / D1).
 *
 * Single ordered spine; columns/links carry a `markets: Market[]` filter so
 * per-market footer structure is derived with `filterByMarket` — counts are
 * never hardcoded per market. Column/link `id`s are the join keys (D2):
 * display strings resolve through `OBELIX_FOOTER_KEYS[id]` → ALH
 * (`ads-footer`), while hrefs (routing) and the media references stay here.
 *
 * Media stays in Contentful per A3; `media` holds the resolved asset
 * references `{ url, contentType }[]` so `FooterMediaGrid`'s existing prop
 * contract is preserved.
 *
 * Seeded from the Contentful staging snapshot committed at
 * `tests/parity/fixtures/footer.json`; `tests/parity/configDrift.test.ts`
 * holds the config to it.
 */
import { ALL_MARKETS, type Market } from "@components/shared/navigation/navConfig";

/**
 * Contentful locales whose localized `links` list carries the en-only links
 * (verified against the staging snapshot: the Resources group lists "Setup
 * and Activation" only for the en-* locales).
 */
const EN_LOCALES = ["en-US", "en-GB", "en-AU", "en-CA"] as const;
/** Supported locales other than pt-BR (BR renders localized bottom links). */
const NON_PT_LOCALES = [
  "en-US",
  "en-GB",
  "en-AU",
  "en-CA",
  "de-DE",
  "es-ES",
  "es-MX",
  "fr-FR",
  "it-IT",
  "ja-JP",
  "ko-KR",
] as const;

/** Resolved Contentful media reference (the asset itself stays in the CMS). */
export interface FooterMediaRef {
  url: string;
  contentType: string;
}

export interface FooterLinkNode {
  id: string;
  /** Key into the `ads-footer` namespace for the link's display text. */
  textKey: string;
  href: string;
  external: boolean;
  markets: readonly Market[];
  /**
   * Contentful locales whose localized `links` list on the source
   * footerLinkGroup includes this link (snapshot `linksByLocale`). Absent =
   * every locale (the group's `links` field falls back to en-US).
   */
  locales?: readonly string[];
}

export interface FooterColumnNode {
  id: string;
  /** Key into the `ads-footer` namespace for the column title. */
  titleKey: string;
  markets: readonly Market[];
  /** Contentful locales the column renders for (unset = every locale). */
  locales?: readonly string[];
  links: readonly FooterLinkNode[];
}

export interface FooterBottomNode {
  id: string;
  titleKey: string;
  markets: readonly Market[];
  links: readonly FooterLinkNode[];
}

export interface FooterConfigShape {
  columns: readonly FooterColumnNode[];
  bottom: FooterBottomNode;
  /** The five newsletter form microcopy strings (plain strings → ALH). */
  newsletter: {
    formLabelKey: string;
    emailFieldLabelKey: string;
    ctaKey: string;
    submissionApprovedKey: string;
    validEmailNoteKey: string;
  };
  /** Singleton strings (cookies / copyright / easter egg). */
  singleton: {
    cookiesKey: string;
    copyrightKey: string;
    easterEggKey: string;
  };
  media: readonly FooterMediaRef[];
}

export interface FooterLinkNode {
  id: string;
  textKey: string;
  href: string;
  external: boolean;
  markets: readonly Market[];
}

export interface FooterColumnNode {
  id: string;
  titleKey: string;
  markets: readonly Market[];
  links: readonly FooterLinkNode[];
}

export interface FooterMediaRef {
  url: string;
  contentType: string;
}

export interface FooterConfig {
  columns: readonly FooterColumnNode[];
  bottom: FooterBottomNode;
  newsletter: {
    formLabelKey: string;
    emailFieldLabelKey: string;
    ctaKey: string;
    submissionApprovedKey: string;
    validEmailNoteKey: string;
  };
  singleton: {
    cookiesKey: string;
    copyrightKey: string;
    easterEggKey: string;
  };
  media: readonly FooterMediaRef[];
}

export interface FooterBottomNode {
  id: string;
  titleKey: string;
  markets: readonly Market[];
  links: readonly FooterLinkNode[];
}

/**
 * The footer spine, seeded from the verified Contentful staging data
 * (footer singleton `3tSOF1Ts7inbZfMM0G8i2j` + link groups
 * `7CubKX75Vz2bCG6iDlMF9L`, `1K5gJffHyZ1MOK1vGTwIpy`, `HFaG0CG92Kcur43GEqxYJ`,
 * bottom `1PKrXpc4rHobLnEeIWKEaE`). Column order here IS the render order.
 */
export const footerConfig = {
  columns: [
    {
      id: "footer-solutions",
      titleKey: "footer-solutions-title",
      markets: ALL_MARKETS,
      links: [
        {
          id: "footer-link-ad-formats",
          textKey: "footer-link-ad-formats",
          href: "/ad-formats",
          external: false,
          markets: ALL_MARKETS,
        },
        {
          id: "footer-link-targeting",
          textKey: "footer-link-targeting",
          href: "/targeting",
          external: false,
          markets: ALL_MARKETS,
        },
        {
          id: "footer-link-measurement-reporting",
          textKey: "footer-link-measurement-reporting",
          href: "/measurement-and-reporting",
          external: false,
          markets: ALL_MARKETS,
        },
      ],
    },
    {
      id: "footer-audience-insights",
      titleKey: "footer-audience-insights-title",
      markets: ALL_MARKETS,
      links: [
        {
          id: "footer-link-insights",
          textKey: "footer-link-insights",
          href: "/insights",
          external: false,
          markets: ALL_MARKETS,
        },
        {
          id: "footer-link-still-watching",
          textKey: "footer-link-still-watching",
          href: "/still-watching",
          external: false,
          markets: ALL_MARKETS,
        },
      ],
    },
    {
      id: "footer-resources",
      titleKey: "footer-resources-title",
      markets: ALL_MARKETS,
      links: [
        {
          id: "footer-link-get-started",
          textKey: "footer-link-get-started",
          href: "/get-connected",
          external: false,
          markets: ALL_MARKETS,
        },
        {
          id: "footer-link-faqs",
          textKey: "footer-link-faqs",
          href: "/faqs",
          external: false,
          markets: ALL_MARKETS,
        },
        {
          id: "footer-link-creative-specs",
          textKey: "footer-link-creative-specs",
          href: "/creative-specs",
          external: false,
          markets: ALL_MARKETS,
        },
        {
          id: "footer-link-setup-activation",
          textKey: "footer-link-setup-activation",
          href: "/set-up-and-activate-your-netflix-campaign",
          external: false,
          markets: ALL_MARKETS,
          // Contentful HFaG0CG92Kcur43GEqxYJ lists this link only for the
          // en-* locales; all other locales' links[] omit it.
          locales: EN_LOCALES,
        },
      ],
    },
  ],
  bottom: {
    // Contentful 1PKrXpc4rHobLnEeIWKEaE
    id: "footer-bottom",
    titleKey: "footer-bottom-title",
    markets: ALL_MARKETS,
    links: [
      {
        // About Netflix — en href; BR renders a localized entry instead.
        id: "footer-link-about-netflix",
        textKey: "footer-link-about-netflix",
        href: "https://about.netflix.com/en",
        external: true,
        markets: ALL_MARKETS,
        locales: NON_PT_LOCALES,
      },
      {
        // Contentful 1bH5u7RNv21irn1QXfY5hB — pt-BR-only About link.
        id: "footer-link-about-netflix-br",
        textKey: "footer-link-about-netflix",
        href: "https://about.netflix.com/pt_br",
        external: true,
        markets: ["BR"],
        locales: ["pt-BR"],
      },
      {
        id: "footer-link-terms",
        textKey: "footer-link-terms",
        href: "terms-and-conditions",
        external: false,
        markets: ALL_MARKETS,
      },
      {
        // Description of Methodology — only en-US / es-ES / it-IT list it in
        // the bottom group's localized links[] (de-DE shows Impressum).
        id: "footer-link-methodology",
        textKey: "footer-link-methodology",
        href: "netflix-description-of-methodology",
        external: false,
        markets: ALL_MARKETS,
        locales: ["en-US", "es-ES", "it-IT"],
      },
      {
        // Contentful 4Aax9iFiYJZB5BJxI5qGLE — DE-only bottom link.
        id: "footer-link-impressum",
        textKey: "footer-link-impressum",
        href: "impressum",
        external: false,
        markets: ["DE"],
        locales: ["de-DE"],
      },
    ],
  },
  newsletter: {
    formLabelKey: "footer-newsletter-form-label",
    emailFieldLabelKey: "footer-newsletter-email-field-label",
    ctaKey: "footer-newsletter-cta",
    submissionApprovedKey: "footer-submission-approved",
    validEmailNoteKey: "footer-valid-email-note",
  },
  singleton: {
    cookiesKey: "footer-cookies",
    copyrightKey: "footer-copyright",
    easterEggKey: "footer-easter-egg-text",
  },
  media: [
    {
      url: "https://videos.ctfassets.net/uc0h52j3d9rs/641JuVcASjZoUBr52kWjx2/bc51525424513cbc0f29e67cb53fa07b/EIP_Video.mp4",
      contentType: "video/mp4",
    },
    {
      url: "https://videos.ctfassets.net/uc0h52j3d9rs/1zqLvvBpJ2KbP9FlbdWhN1/e0a7690bc55a50e0e4ca6682b0123709/SquidGame.mp4",
      contentType: "video/mp4",
    },
    {
      url: "https://videos.ctfassets.net/uc0h52j3d9rs/6VAvmm6wEZLGSuXXbG3aYW/b3b9723e90ebb22ed9ed1a644bb79ff7/OuterBanks.mp4",
      contentType: "video/mp4",
    },
    {
      url: "https://videos.ctfassets.net/uc0h52j3d9rs/3CzRM3CrVipncnpyQ4fH5v/0299636ded10f0b7c25030b84d7ff3fa/Homepage_Global_Hero_Video_Stranger_ThingsC.mp4",
      contentType: "video/mp4",
    },
    {
      url: "https://videos.ctfassets.net/uc0h52j3d9rs/2z0lKXjgJoS4LnEKL1JTEu/f922230b11c2737477804c9d042317d8/Wednesday.mp4",
      contentType: "video/mp4",
    },
  ],
} as const satisfies FooterConfigShape;

/**
 * Derives the ordered footer structure for one market — counts and presence
 * come from the spine's `markets` filters, never from hardcoded per-market
 * lists.
 */
export function filterByMarket(
  config: FooterConfig,
  market: Market,
  /** Current Contentful locale id (e.g. "ja-JP"); locale-scoped nodes only
   * render when their `locales` list contains it. Unset = no locale filter. */
  locale?: string,
): FooterConfig {
  const localeAllows = (link: FooterLinkNode): boolean =>
    !link.locales || (locale != null && link.locales.includes(locale));
  const filterLinks = (links: readonly FooterLinkNode[]): FooterLinkNode[] =>
    links.filter((link) => link.markets.includes(market) && localeAllows(link));
  return {
    columns: config.columns
      .filter((column) => column.markets.includes(market))
      .map((column) => ({ ...column, links: filterLinks(column.links) })),
    bottom: { ...config.bottom, links: config.bottom.links.filter((l) => l.markets.includes(market) && localeAllows(l)) },
    newsletter: config.newsletter,
    singleton: config.singleton,
    media: config.media,
  };
}

/**
 * Union of every display-string key the footer spine uses (column titles,
 * link text keys, newsletter microcopy, singleton strings) — the join-key
 * space shared with `OBELIX_FOOTER_KEYS`.
 */
export type FooterTextKey =
  | (typeof footerConfig["columns"])[number]["titleKey"]
  | (typeof footerConfig["columns"])[number]["links"][number]["textKey"]
  | (typeof footerConfig["bottom"])["titleKey"]
  | (typeof footerConfig["bottom"])["links"][number]["textKey"]
  | (typeof footerConfig["newsletter"])[keyof (typeof footerConfig)["newsletter"]]
  | (typeof footerConfig["singleton"])[keyof (typeof footerConfig)["singleton"]];
